import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { AxiosError } from "axios";
import { NuqsAdapter } from "nuqs/adapters/tanstack-router";
import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { toast } from "sonner";
// Generated Routes
import { routeTree } from "./routeTree.gen";
import { DirectionProvider } from "./settings/context/direction-provider";
import { FontProvider } from "./settings/context/font-provider";
import { ThemeProvider } from "./theme/theme-provider";

// Styles
// import "@workspace/ui/globals.css";
import "@/styles/index.css";
import { Loader2 } from "lucide-react";
import { orpc } from "./api/orpc";
import { NotFoundError } from "./features/errors/not-found-error";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // eslint-disable-next-line no-console
        if (import.meta.env.DEV) console.log({ failureCount, error });

        if (failureCount >= 0 && import.meta.env.DEV) return false;
        if (failureCount > 3 && import.meta.env.PROD) return false;

        return !(error instanceof AxiosError && [401, 403].includes(error.response?.status ?? 0));
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s,
    },
    mutations: {
      onError: (error) => {
        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast.error("Content not modified!");
          }
        }
      },
    },
  },
});

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient, orpc },
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFoundError,
  defaultPendingComponent: () => (
    <div className="flex h-screen w-screen items-center justify-center">
      <Loader2 className="animate-spin" />
    </div>
  ),
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById("root");
if (rootElement === null) throw new Error("No root ");
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <FontProvider>
            <DirectionProvider>
              <NuqsAdapter>
                <RouterProvider router={router} />
              </NuqsAdapter>
            </DirectionProvider>
          </FontProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}
